import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, QrCode } from 'lucide-react';
import { authService } from '../services/auth';
import { useAuth } from '../contexts/AuthContext';

type Step = 'form' | 'verify';

interface CognitoErrorLike {
  name?: string;
  message?: string;
}

export function SignupPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [step, setStep] = useState<Step>('form');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle');

  const reset = () => {
    setStep('form');
    setCode('');
    setError('');
    setResendStatus('idle');
  };

  const finalizeSignIn = async () => {
    await authService.signInWithPassword(email, password);
    await refreshUser();
    navigate('/dashboard');
  };

  const handleFormSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { userConfirmed } = await authService.signUp(email, password);
      if (userConfirmed) {
        await finalizeSignIn();
      } else {
        setStep('verify');
      }
    } catch (err) {
      setError(friendlyError(err, 'signup'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authService.confirmSignUp(email, code);
      await finalizeSignIn();
    } catch (err) {
      setError(friendlyError(err, 'confirm'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendStatus('sending');
    setError('');
    try {
      await authService.resendSignUpCode(email);
      setResendStatus('sent');
    } catch (err) {
      setResendStatus('idle');
      setError(friendlyError(err, 'resend'));
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8 gap-3">
          <div className="w-12 h-12 bg-surface border border-border rounded-xl flex items-center justify-center">
            <QrCode className="w-6 h-6 text-accent" strokeWidth={2} />
          </div>
          <div className="text-center">
            <h1 className="text-xl font-semibold text-foreground">{titleFor(step)}</h1>
            <p className="text-sm text-text-muted mt-1">{subtitleFor(step, email)}</p>
          </div>
        </div>

        {step === 'form' && (
          <form onSubmit={handleFormSubmit} className="bg-surface border border-border rounded-xl p-6 space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-sm font-medium text-foreground">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full bg-muted border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-150"
                placeholder="you@example.com"
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="block text-sm font-medium text-foreground">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full bg-muted border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-150"
                placeholder="At least 8 characters"
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg py-2.5 text-sm transition-colors duration-150 cursor-pointer flex items-center justify-center gap-2"
            >
              {loading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {loading ? 'Creating account…' : 'Create account'}
            </button>

            <p className="text-xs text-text-muted text-center pt-1">
              Already have an account?{' '}
              <Link to="/login" className="text-accent hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        )}

        {step === 'verify' && (
          <form onSubmit={handleVerifySubmit} className="bg-surface border border-border rounded-xl p-6 space-y-4">
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1 text-xs font-medium text-text-muted hover:text-foreground transition-colors duration-150 cursor-pointer"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Use a different email
            </button>

            <div className="space-y-1.5">
              <label htmlFor="code" className="block text-sm font-medium text-foreground">
                Confirmation code
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="\d{6,8}"
                maxLength={8}
                required
                autoFocus
                autoComplete="one-time-code"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                className="w-full bg-muted border border-border rounded-lg px-3 py-2.5 text-base font-mono tracking-[0.4em] text-center text-foreground placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-150"
                placeholder="••••••"
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <button
              type="submit"
              disabled={loading || code.length < 6}
              className="w-full bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg py-2.5 text-sm transition-colors duration-150 cursor-pointer flex items-center justify-center gap-2"
            >
              {loading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {loading ? 'Verifying…' : 'Confirm & sign in'}
            </button>

            <p className="text-xs text-text-muted text-center pt-1">
              Didn't get the code?{' '}
              <button
                type="button"
                onClick={handleResend}
                disabled={resendStatus === 'sending'}
                className="text-accent hover:underline disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {resendStatus === 'sending' ? 'Sending…' : resendStatus === 'sent' ? 'Sent — check again' : 'Resend it'}
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function titleFor(step: Step): string {
  if (step === 'form') return 'Create your account';
  return 'Confirm your email';
}

function subtitleFor(step: Step, email: string): string {
  if (step === 'form') return 'Sign up with email and a password';
  return `We sent a code to ${email}`;
}

function friendlyError(err: unknown, kind: 'signup' | 'confirm' | 'resend'): string {
  const e = err as CognitoErrorLike;
  const name = e.name ?? '';
  if (name === 'UsernameExistsException') {
    return 'An account with that email already exists. Try signing in instead.';
  }
  if (name === 'InvalidPasswordException') {
    return e.message ?? 'Password does not meet the requirements';
  }
  if (name === 'InvalidParameterException') {
    return e.message ?? 'Please check the email address and try again';
  }
  if (name === 'CodeMismatchException') return 'That code is incorrect';
  if (name === 'ExpiredCodeException') return 'That code expired — request a new one';
  if (name === 'LimitExceededException') return 'Too many attempts. Try again in a few minutes.';
  if (name === 'TooManyRequestsException') return 'Too many requests. Try again in a moment.';
  if (kind === 'resend' && name === 'NotAuthorizedException') {
    return 'This account is already confirmed — try signing in.';
  }
  return e.message ?? 'Something went wrong';
}
